/**
 * ScientificSymbolPicker
 * ──────────────────────
 * The universal "Σ" picker used in BOTH the Student Tutor composer and the
 * Teacher Editor chat. It opens a friendly grid of math, physics, chemistry
 * and logic symbols and inserts the selected token into a target <textarea>
 * at the current cursor position (wrapped in `$...$` so the KaTeX renderer
 * echoes them back as real symbols).
 *
 * Improvements over the previous student-only `MathSymbolPopover.jsx`
 *   • Bigger, more accessible touch targets (h-11 vs h-9).
 *   • Search across all groups — type "alpha" or "→" or "ce" to filter.
 *   • Recently used row (persisted in localStorage) so the next equation is
 *     one click away.
 *   • Chemistry tab with `\ce{...}` macros (mhchem), arrows, equilibrium.
 *   • Physics constants & units tab.
 *   • Greek, Operators, Structures (frac/sqrt/sum/int/lim), Sets & logic.
 *   • Theme-aware: respects `--st-primary`, `--st-bg`, `--st-border` if
 *     present; otherwise falls back to Deskey green.
 *   • Single dependency: `lucide-react` icons.
 *
 * Props
 *   textareaRef        ref to the target <textarea>
 *   onChange           setter that receives the new full value
 *   align              'left' | 'right' — PREFERRED horizontal placement
 *   anchor             'bottom' | 'top' — PREFERRED vertical side ('bottom'
 *                      opens upward, 'top' opens downward). The panel is
 *                      fixed-positioned and auto-flips/clamps so it always
 *                      stays fully inside the viewport, whatever the
 *                      preference says.
 *   className          extra wrapper classes
 *   testIdPrefix       data-testid base (defaults to 'sym-picker')
 *   accent             optional override colour
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Sigma, X, Search, Sparkles } from 'lucide-react';

const GROUPS = [
  {
    id: 'greek', label: 'Greek',
    items: [
      ['α', '\\alpha'], ['β', '\\beta'], ['γ', '\\gamma'], ['δ', '\\delta'],
      ['ε', '\\epsilon'], ['ζ', '\\zeta'], ['η', '\\eta'], ['θ', '\\theta'],
      ['κ', '\\kappa'], ['λ', '\\lambda'], ['μ', '\\mu'], ['ν', '\\nu'],
      ['π', '\\pi'], ['ρ', '\\rho'], ['σ', '\\sigma'], ['τ', '\\tau'],
      ['φ', '\\phi'], ['χ', '\\chi'], ['ψ', '\\psi'], ['ω', '\\omega'],
      ['Δ', '\\Delta'], ['Σ', '\\Sigma'], ['Π', '\\Pi'], ['Ω', '\\Omega'],
      ['Γ', '\\Gamma'], ['Λ', '\\Lambda'], ['Φ', '\\Phi'], ['Ψ', '\\Psi'],
    ],
  },
  {
    id: 'ops', label: 'Operators',
    items: [
      ['±', '\\pm'], ['∓', '\\mp'], ['×', '\\times'], ['÷', '\\div'],
      ['·', '\\cdot'], ['∗', '\\ast'], ['⊕', '\\oplus'], ['⊗', '\\otimes'],
      ['≈', '\\approx'], ['≠', '\\ne'], ['≡', '\\equiv'], ['≤', '\\le'],
      ['≥', '\\ge'], ['≪', '\\ll'], ['≫', '\\gg'], ['∝', '\\propto'],
      ['→', '\\to'], ['←', '\\leftarrow'], ['↔', '\\leftrightarrow'],
      ['⇒', '\\Rightarrow'], ['⇔', '\\Leftrightarrow'], ['↦', '\\mapsto'],
      ['∞', '\\infty'], ['∇', '\\nabla'], ['∂', '\\partial'], ['∑', '\\sum'],
    ],
  },
  {
    id: 'struct', label: 'Structures',
    items: [
      ['a/b', '\\frac{a}{b}', 'fraction'],
      ['√x', '\\sqrt{x}', 'sqrt'],
      ['ⁿ√x', '\\sqrt[n]{x}', 'nth root'],
      ['x²', 'x^{2}', 'square'],
      ['xⁿ', 'x^{n}', 'power'],
      ['xᵢ', 'x_{i}', 'subscript'],
      ['∑', '\\sum_{i=1}^{n}', 'sum'],
      ['∏', '\\prod_{i=1}^{n}', 'product'],
      ['∫', '\\int_{a}^{b}', 'integral'],
      ['∮', '\\oint', 'closed integral'],
      ['lim', '\\lim_{x \\to 0}', 'limit'],
      ['log', '\\log', 'log'],
      ['ln',  '\\ln', 'natural log'],
      ['sin', '\\sin'], ['cos', '\\cos'], ['tan', '\\tan'],
      ['(a)', '\\left( a \\right)', 'parens'],
      ['|a|', '\\left| a \\right|', 'abs'],
      ['vec', '\\vec{v}', 'vector'],
      ['mat', '\\begin{bmatrix} a & b \\\\ c & d \\end{bmatrix}', 'matrix'],
    ],
  },
  {
    id: 'chem', label: 'Chemistry',
    items: [
      ['H₂O', '\\ce{H2O}', 'water'],
      ['CO₂', '\\ce{CO2}', 'carbon dioxide'],
      ['O₂', '\\ce{O2}'],
      ['NaCl', '\\ce{NaCl}'],
      ['H₂SO₄', '\\ce{H2SO4}'],
      ['HCl', '\\ce{HCl}'],
      ['NH₃', '\\ce{NH3}'],
      ['→', '\\ce{->}', 'reaction'],
      ['⇌', '\\ce{<=>}', 'equilibrium'],
      ['↑',  '\\ce{^}',  'gas evolves'],
      ['↓',  '\\ce{v}',  'precipitate'],
      ['Δ', '\\ce{\\Delta}', 'heat'],
      ['rxn', '\\ce{2H2 + O2 -> 2H2O}', 'water synthesis'],
      ['acid+base', '\\ce{HCl + NaOH -> NaCl + H2O}', 'neutralization'],
      ['mol', '\\text{mol}'],
      ['M (molar)', '\\text{M}'],
      ['ΔH', '\\Delta H', 'enthalpy'],
      ['Ka', 'K_a', 'acid constant'],
    ],
  },
  {
    id: 'physics', label: 'Physics',
    items: [
      ['F=ma', 'F = ma', 'newton 2nd'],
      ['E=mc²', 'E = mc^{2}', 'mass-energy'],
      ['v=u+at', 'v = u + at', 'kinematics'],
      ['KE', '\\tfrac{1}{2} m v^{2}', 'kinetic energy'],
      ['PE', 'mgh', 'potential energy'],
      ['λ', '\\lambda', 'wavelength'],
      ['ν', '\\nu', 'frequency'],
      ['ℏ', '\\hbar', 'reduced Planck'],
      ['ω', '\\omega', 'angular freq'],
      ['μ₀', '\\mu_{0}'],
      ['ε₀', '\\varepsilon_{0}'],
      ['m/s', '\\text{m/s}'],
      ['m/s²', '\\text{m/s}^{2}'],
      ['Ω', '\\Omega', 'ohm'],
      ['°C', '^{\\circ}\\text{C}'],
      ['ρ', '\\rho', 'density'],
      ['τ', '\\tau', 'torque'],
    ],
  },
  {
    id: 'sets', label: 'Sets & Logic',
    items: [
      ['∈', '\\in'], ['∉', '\\notin'], ['⊂', '\\subset'], ['⊆', '\\subseteq'],
      ['∪', '\\cup'], ['∩', '\\cap'], ['∅', '\\emptyset'], ['∁', '\\complement'],
      ['ℝ', '\\mathbb{R}'], ['ℕ', '\\mathbb{N}'], ['ℤ', '\\mathbb{Z}'], ['ℚ', '\\mathbb{Q}'],
      ['ℂ', '\\mathbb{C}'],
      ['∀', '\\forall'], ['∃', '\\exists'], ['∄', '\\nexists'],
      ['¬', '\\neg'], ['∧', '\\land'], ['∨', '\\lor'],
      ['⊕', '\\oplus'], ['⊢', '\\vdash'], ['⊨', '\\models'],
    ],
  },
];

const ALL_ITEMS = GROUPS.flatMap(g => g.items.map(([label, token, hint]) => ({ label, token, hint: hint || '', group: g.id })));

const RECENTS_KEY = 'desky_sym_recents';
function loadRecents() {
  try { return JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]').slice(0, 12); } catch { return []; }
}
function saveRecent(token, label) {
  try {
    const cur = loadRecents();
    const next = [{ label, token }, ...cur.filter(r => r.token !== token)].slice(0, 12);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch { /* ignore */ }
}

export default function ScientificSymbolPicker({
  textareaRef,
  onChange,
  align = 'right',
  anchor = 'bottom',
  className = '',
  testIdPrefix = 'sym-picker',
  accent = '#34A853',
  size = 'md',
}) {
  const [open, setOpen] = useState(false);
  const [group, setGroup] = useState('greek');
  const [q, setQ] = useState('');
  const [recents, setRecents] = useState(loadRecents);
  const popRef = useRef(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  // Fixed-position coords for the panel; null until the first measurement
  // (the panel renders hidden for that one frame so it never flashes in the
  // wrong spot).
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Keep the panel fully inside the viewport. `position: fixed` frees it
  // from overflow-hidden ancestors (e.g. the tutor chat pane); we honour the
  // preferred anchor/align, flip vertically when there isn't room, and clamp
  // both axes to the viewport. Re-measured whenever the contents change size
  // (search results, group tabs, recents) and on window resize/scroll.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const place = () => {
      const btn = btnRef.current;
      const panel = panelRef.current;
      if (!btn || !panel) return;
      const b = btn.getBoundingClientRect();
      const pw = panel.offsetWidth || 360;
      const ph = panel.offsetHeight || 420;
      const margin = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let left = align === 'right' ? b.right - pw : b.left;
      left = Math.max(margin, Math.min(left, vw - pw - margin));
      const below = b.bottom + margin;
      const above = b.top - ph - margin;
      let top = anchor === 'top' ? below : above;
      if (top + ph > vh - margin) top = above;   // no room below → open upward
      if (top < margin) top = below;             // no room above → open downward
      top = Math.max(margin, Math.min(top, vh - ph - margin));
      setPos({ left, top });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, align, anchor, q, group, recents]);

  // Search across all items; falls back to current group when query empty.
  const items = useMemo(() => {
    const trimmed = q.trim().toLowerCase();
    if (!trimmed) {
      const g = GROUPS.find(g => g.id === group) || GROUPS[0];
      return g.items.map(([label, token, hint]) => ({ label, token, hint: hint || '' }));
    }
    return ALL_ITEMS.filter(it => (
      it.label.toLowerCase().includes(trimmed) ||
      it.token.toLowerCase().includes(trimmed) ||
      it.hint.toLowerCase().includes(trimmed)
    )).slice(0, 40);
  }, [q, group]);

  const insert = (token, label) => {
    const ta = textareaRef?.current;
    if (!ta) return;
    const value = ta.value || '';
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    const before = value.slice(0, start);
    const after = value.slice(end);
    // If we're already inside a `$...$` block, just drop the raw token.
    const inMath = (before.match(/\$/g) || []).length % 2 === 1;
    const piece = inMath ? token : `$${token}$`;
    const next = before + piece + after;
    onChange(next);
    saveRecent(token, label);
    setRecents(loadRecents());
    requestAnimationFrame(() => {
      const pos = start + piece.length;
      ta.focus();
      try { ta.setSelectionRange(pos, pos); } catch { /* ignore */ }
    });
  };

  const sizeClass = size === 'sm' ? 'w-9 h-9' : 'w-10 h-10';

  return (
    <div className={`relative ${className}`} ref={popRef}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid={`${testIdPrefix}-toggle`}
        title="Insert scientific symbol (Σ, π, →, ∫, \\ce{H2O} …)"
        className={`${sizeClass} rounded-full flex items-center justify-center border-2 transition-all hover:-translate-y-0.5 active:scale-95 flex-shrink-0`}
        style={{
          borderColor: open ? accent : 'rgba(0,0,0,0.10)',
          background: open ? `${accent}14` : 'transparent',
          color: open ? accent : '#475569',
        }}>
        <Sigma className="w-4 h-4" />
      </button>

      {open && (
        <div
          ref={panelRef}
          className="fixed w-[360px] max-w-[calc(100vw-16px)] z-50 rounded-2xl bg-white overflow-hidden shadow-2xl border border-slate-200"
          data-testid={`${testIdPrefix}-panel`}
          style={{
            left: pos ? pos.left : 0,
            top: pos ? pos.top : 0,
            visibility: pos ? 'visible' : 'hidden',
            boxShadow: '0 20px 50px -12px rgba(15,23,42,0.22), 0 6px 18px -6px rgba(15,23,42,0.10)',
          }}>
          {/* Header */}
          <div className="px-3 py-2.5 flex items-center justify-between border-b border-slate-100 bg-gradient-to-b from-white to-slate-50/60">
            <div className="flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" style={{ color: accent }} />
              <span className="text-[10.5px] uppercase tracking-[0.18em] font-extrabold text-slate-700">Scientific symbols</span>
            </div>
            <button onClick={() => setOpen(false)} className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors" aria-label="Close">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Search */}
          <div className="px-3 pt-2.5">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search alpha, delta, →, ∫, H2O …"
                data-testid={`${testIdPrefix}-search`}
                className="w-full h-8 pl-7 pr-2.5 rounded-lg text-[12px] bg-slate-50 border border-slate-200 outline-none focus:bg-white focus:ring-2 focus:ring-emerald-400/30 transition-all placeholder:text-slate-400"
              />
            </div>
          </div>

          {/* Recents */}
          {!q && recents.length > 0 && (
            <div className="px-3 pt-2.5">
              <p className="text-[9.5px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Recents</p>
              <div className="flex flex-wrap gap-1">
                {recents.map(r => (
                  <button
                    key={r.token}
                    onClick={() => insert(r.token, r.label)}
                    className="px-2 h-8 rounded-lg text-[13px] font-bold bg-slate-50 border border-slate-200 hover:bg-emerald-50 hover:border-emerald-300 hover:-translate-y-0.5 transition-all"
                    style={{ fontFamily: 'serif' }}
                    data-testid={`${testIdPrefix}-recent-${r.label}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Group tabs */}
          {!q && (
            <div className="px-3 pt-2.5 flex gap-1 flex-wrap">
              {GROUPS.map(g => (
                <button
                  key={g.id}
                  onClick={() => setGroup(g.id)}
                  data-testid={`${testIdPrefix}-tab-${g.id}`}
                  className="px-2.5 py-1 rounded-full text-[10.5px] font-bold transition-all"
                  style={{
                    background: group === g.id ? accent : 'transparent',
                    color: group === g.id ? '#fff' : '#475569',
                    border: `1px solid ${group === g.id ? accent : '#e2e8f0'}`,
                  }}>
                  {g.label}
                </button>
              ))}
            </div>
          )}

          {/* Symbol grid */}
          <div className="p-2.5 grid grid-cols-6 gap-1.5 max-h-[260px] overflow-y-auto">
            {items.length === 0 && (
              <div className="col-span-6 py-6 text-center text-[11px] text-slate-400">No symbols match &ldquo;{q}&rdquo;</div>
            )}
            {items.map(({ label, token, hint }) => (
              <button
                key={label + token}
                onClick={() => insert(token, label)}
                data-testid={`${testIdPrefix}-sym-${label}`}
                title={hint ? `${token}  —  ${hint}` : token}
                className="h-11 rounded-lg flex items-center justify-center text-[15px] font-bold text-slate-800 bg-slate-50 border border-slate-200 hover:bg-emerald-50 hover:border-emerald-300 hover:-translate-y-0.5 hover:shadow-sm transition-all active:scale-95"
                style={{ fontFamily: 'serif' }}>
                {label}
              </button>
            ))}
          </div>

          {/* Footer */}
          <div className="px-3 py-2 text-[10px] text-slate-400 border-t border-slate-100 bg-slate-50/60 leading-snug">
            Inserts at cursor wrapped in <code className="px-1 py-0.5 rounded bg-white border border-slate-200 text-slate-600">{'$…$'}</code> — renders as real symbols in replies.
          </div>
        </div>
      )}
    </div>
  );
}
